import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import AdminSidebar from "@/components/dashboard/AdminSidebar";
import { dashboardItems } from "@/data/dashboardData";
import { useCategories, Category } from "@/hooks/useCategories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Eye, EyeOff, GripVertical, ExternalLink } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const AVAILABLE_ICONS = [
  "Calendar", "CalendarDays", "GanttChart", "PartyPopper", "Sparkles", 
  "Users", "Flag", "Globe2", "AppWindow", "Twitter", "Radio", 
  "MessageCircle", "Building", "Star", "Heart", "Music", "Camera",
  "Video", "Book", "Gift", "Award", "Mic", "Trophy", "Zap"
];

const AVAILABLE_COLORS = [
  { value: "text-blue-500", label: "أزرق" },
  { value: "text-orange-500", label: "برتقالي" },
  { value: "text-pink-500", label: "وردي" },
  { value: "text-green-500", label: "أخضر" },
  { value: "text-purple-500", label: "بنفسجي" },
  { value: "text-teal-500", label: "تركوازي" },
  { value: "text-indigo-500", label: "نيلي" },
  { value: "text-cyan-500", label: "سماوي" },
  { value: "text-gray-900", label: "أسود" },
  { value: "text-rose-500", label: "أحمر" },
  { value: "text-emerald-500", label: "زمردي" },
  { value: "text-amber-500", label: "كهرماني" },
];

// Sortable Category Card Component
interface SortableCategoryCardProps {
  category: Category;
  isRTL: boolean;
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (category: Category) => void;
  getIconComponent: (iconName: string) => React.ComponentType<{ size?: number; className?: string }>;
}

const SortableCategoryCard = ({ 
  category, 
  isRTL, 
  onEdit, 
  onDelete, 
  onToggleVisibility,
  getIconComponent 
}: SortableCategoryCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const IconComponent = getIconComponent(category.icon);

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`relative rounded-xl border border-white/10 bg-white/[0.08] backdrop-blur-sm overflow-hidden ${!category.is_visible ? 'opacity-50' : ''} ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* System badge */}
      {category.is_system && (
        <span className="absolute top-2 left-2 text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded z-10">
          {isRTL ? "نظام" : "System"}
        </span>
      )}

      <div className="p-4 flex flex-col items-center text-center gap-3">
        {/* Icon */}
        <div className={`${category.color} p-3 rounded-lg bg-white/10`}>
          <IconComponent size={28} />
        </div>

        {/* Name & Path */}
        <div>
          <h3 className="font-semibold text-white">{category.name_ar}</h3>
          <p className="text-sm text-white/50">{category.name_en}</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <Link to={category.path} target="_blank" className="text-primary hover:text-primary/80">
              <ExternalLink className="w-3 h-3" />
            </Link>
            <p className="text-xs text-white/40 font-mono">{category.path}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 pt-1 border-t border-white/10 w-full justify-center">
          {!category.is_system && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent dir={isRTL ? "rtl" : "ltr"}>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isRTL ? "حذف القسم" : "Delete Category"}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isRTL 
                      ? `هل أنت متأكد من حذف قسم "${category.name_ar}"؟ لا يمكن التراجع عن هذا الإجراء.`
                      : `Are you sure you want to delete "${category.name_en}"? This action cannot be undone.`
                    }
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => onDelete(category.id)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    {isRTL ? "حذف" : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" onClick={() => onEdit(category)}>
            <Pencil className="w-4 h-4 text-blue-500" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-white/10"
            onClick={() => onToggleVisibility(category)}
            title={category.is_visible ? "إخفاء" : "إظهار"}
          >
            {category.is_visible ? (
              <Eye className="w-4 h-4 text-green-500" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-400" />
            )}
          </Button>

          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded touch-none h-8 w-8 flex items-center justify-center"
            title={isRTL ? "اسحب لتغيير الترتيب" : "Drag to reorder"}
          >
            <GripVertical className="w-4 h-4 text-white/40" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoriesManagement = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { categories, isLoading, addCategory, updateCategory, deleteCategory, toggleVisibility, updateOrder } = useCategories();
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [existingSlugs, setExistingSlugs] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    icon: "Calendar",
    color: "text-blue-500",
    path: "",
    category_slug: "",
    events_limit: 6,
  });

  // Fetch existing category slugs from scraped_events
  React.useEffect(() => {
    const fetchExistingSlugs = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("scraped_events")
        .select("category")
        .not("category", "is", null);
      
      if (data) {
        const uniqueSlugs = [...new Set(data.map(e => e.category).filter(Boolean))] as string[];
        setExistingSlugs(uniqueSlugs.sort());
      }
    };
    fetchExistingSlugs();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);
      
      const newOrder = arrayMove(categories, oldIndex, newIndex);
      
      // Update display_order for all categories
      const orderedCategories = newOrder.map((cat, index) => ({
        id: cat.id,
        display_order: index + 1,
      }));
      
      updateOrder.mutate(orderedCategories);
    }
  };

  const resetForm = () => {
    setFormData({
      name_ar: "",
      name_en: "",
      icon: "Calendar",
      color: "text-blue-500",
      path: "",
      category_slug: "",
      events_limit: 6,
    });
    setEditingCategory(null);
  };

  const handleSubmit = async () => {
    // For new categories, use /category/ prefix for dynamic routing
    let pathValue = formData.path.startsWith("/") ? formData.path : `/${formData.path}`;
    
    // If it's a new non-system category and doesn't already have /category/ prefix
    if (!editingCategory && !pathValue.startsWith("/category/")) {
      const slug = pathValue.replace("/", "");
      pathValue = `/category/${slug}`;
    }

    // Generate category_slug from path for event filtering (only if not manually set)
    const generateCategorySlug = (path: string): string => {
      // Handle /category/slug format
      if (path.startsWith("/category/")) {
        return path.replace("/category/", "");
      }
      // Handle /slug format
      return path.replace("/", "");
    };

    // Use manual category_slug if provided (not _auto_), otherwise generate from path
    const categorySlug = (formData.category_slug.trim() && formData.category_slug !== "_auto_") 
      ? formData.category_slug.trim() 
      : generateCategorySlug(pathValue);
    
    if (editingCategory) {
      await updateCategory.mutateAsync({
        id: editingCategory.id,
        name_ar: formData.name_ar,
        name_en: formData.name_en,
        icon: formData.icon,
        color: formData.color,
        path: pathValue,
        category_slug: categorySlug,
        events_limit: formData.events_limit,
      });
      toast.success(isRTL ? "تم تحديث القسم بنجاح" : "Category updated successfully");
    } else {
      const maxOrder = Math.max(...categories.map(c => c.display_order), 0);
      await addCategory.mutateAsync({
        name_ar: formData.name_ar,
        name_en: formData.name_en,
        icon: formData.icon,
        color: formData.color,
        path: pathValue,
        category_slug: categorySlug,
        is_visible: true,
        display_order: maxOrder + 1,
        is_system: false,
        events_limit: formData.events_limit,
      });
      toast.success(isRTL ? "تم إضافة القسم وإنشاء صفحة خاصة به" : "Category added with its dedicated page");
    }
    
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name_ar: category.name_ar,
      name_en: category.name_en,
      icon: category.icon,
      color: category.color,
      path: category.path,
      category_slug: category.category_slug || "",
      events_limit: category.events_limit || 6,
    });
    setIsAddDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteCategory.mutateAsync(id);
  };

  const handleToggleVisibility = async (category: Category) => {
    await toggleVisibility.mutateAsync({ id: category.id, is_visible: !category.is_visible });
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Calendar;
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full" dir={isRTL ? "rtl" : "ltr"} style={{background: 'linear-gradient(135deg, #1a0f22, #241530, #1a0f22)' }}>
        <AdminSidebar menuItems={dashboardItems} />
        <SidebarInset className="flex-1 bg-transparent">
          <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {isRTL ? "إدارة أيقونات الأقسام" : "Categories Management"}
                </h1>
                <p className="text-white/50 mt-1">
                  {isRTL ? "إضافة، تعديل، وحذف أقسام التصفح" : "Add, edit, and delete navigation categories"}
                </p>
              </div>
              
              <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
                setIsAddDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 ml-2" />
                    {isRTL ? "إضافة قسم" : "Add Category"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" dir={isRTL ? "rtl" : "ltr"}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingCategory 
                        ? (isRTL ? "تعديل القسم" : "Edit Category")
                        : (isRTL ? "إضافة قسم جديد" : "Add New Category")
                      }
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{isRTL ? "الاسم بالعربية" : "Arabic Name"}</Label>
                        <Input
                          value={formData.name_ar}
                          onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                          placeholder="الفعاليات"
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{isRTL ? "الاسم بالإنجليزية" : "English Name"}</Label>
                        <Input
                          value={formData.name_en}
                          onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                          placeholder="Events"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{isRTL ? "الرابط" : "Path"}</Label>
                      <Input
                        value={formData.path}
                        onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                        placeholder="/events"
                        dir="ltr"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{isRTL ? "معرّف التصنيف (category_slug)" : "Category Slug"}</Label>
                      <Select 
                        value={formData.category_slug} 
                        onValueChange={(value) => setFormData({ ...formData, category_slug: value === "_custom_" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isRTL ? "اختر أو اكتب قيمة جديدة" : "Select or enter custom"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_auto_">
                            <span className="text-muted-foreground">
                              {isRTL ? "توليد تلقائي من الرابط" : "Auto-generate from path"}
                            </span>
                          </SelectItem>
                          {existingSlugs.map((slug) => (
                            <SelectItem key={slug} value={slug}>
                              {slug}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={formData.category_slug === "_auto_" ? "" : formData.category_slug}
                        onChange={(e) => setFormData({ ...formData, category_slug: e.target.value })}
                        placeholder={isRTL ? "أو اكتب قيمة مخصصة" : "Or enter custom value"}
                        dir="ltr"
                        className="mt-2"
                      />
                      <p className="text-xs text-muted-foreground">
                        {isRTL 
                          ? "يُستخدم لربط القسم بالفعاليات في قاعدة البيانات" 
                          : "Used to link the category with events in the database"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>{isRTL ? "الأيقونة" : "Icon"}</Label>
                      <Select value={formData.icon} onValueChange={(value) => setFormData({ ...formData, icon: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {AVAILABLE_ICONS.map((iconName) => {
                            const IconComp = getIconComponent(iconName);
                            return (
                              <SelectItem key={iconName} value={iconName}>
                                <div className="flex items-center gap-2">
                                  <IconComp className="w-4 h-4" />
                                  <span>{iconName}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{isRTL ? "اللون" : "Color"}</Label>
                      <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AVAILABLE_COLORS.map((color) => {
                            const PreviewIcon = getIconComponent(formData.icon);
                            return (
                              <SelectItem key={color.value} value={color.value}>
                                <div className="flex items-center gap-2">
                                  <PreviewIcon className={`w-4 h-4 ${color.value}`} />
                                  <span>{color.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{isRTL ? "عدد الفعاليات المعروضة" : "Events Limit"}</Label>
                      <Select 
                        value={formData.events_limit.toString()} 
                        onValueChange={(value) => setFormData({ ...formData, events_limit: parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[3, 4, 6, 8, 9, 12].map((num) => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} {isRTL ? "فعاليات" : "events"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preview */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label className="mb-3 block">{isRTL ? "معاينة" : "Preview"}</Label>
                      <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border">
                        <div className={`${formData.color} p-3 rounded-lg bg-gray-50`}>
                          {React.createElement(getIconComponent(formData.icon), { size: 32 })}
                        </div>
                        <span className="text-sm font-medium text-gray-700 mt-2">
                          {formData.name_ar || "اسم القسم"}
                        </span>
                        <span className="text-xs text-gray-400 mt-1">
                          {formData.events_limit} {isRTL ? "فعاليات" : "events"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setIsAddDialogOpen(false); resetForm(); }}>
                      {isRTL ? "إلغاء" : "Cancel"}
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      disabled={!formData.name_ar || !formData.name_en || !formData.path}
                    >
                      {editingCategory 
                        ? (isRTL ? "تحديث" : "Update")
                        : (isRTL ? "إضافة" : "Add")
                      }
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              <>
                <p className="text-sm text-white/50 mb-4 flex items-center gap-2">
                  <GripVertical className="w-4 h-4" />
                  {isRTL ? "اسحب البطاقات لتغيير ترتيب الأقسام" : "Drag cards to reorder categories"}
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={categories.map(c => c.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categories.map((category) => (
                        <SortableCategoryCard
                          key={category.id}
                          category={category}
                          isRTL={isRTL}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onToggleVisibility={handleToggleVisibility}
                          getIconComponent={getIconComponent}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            )}
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default CategoriesManagement;
